import type { ServiceWorkerListRecord } from '@navio/nsdk';
import { useStore } from '@tanstack/react-form';
import { invalidateQuery, noddiAsync, URLKeys } from 'noddi-async';
import { NoddiBasicCard, NoddiCard, NoddiSheet, useAppForm } from 'noddi-ui-web';
import { pluralizeWithValue } from 'noddi-util';
import { z } from 'zod';
import { TanstackSkillsAsyncMultiSelect } from '../../form/tanstack/features/Skills/TanstackSkillsAsyncMultiSelect';
import { CapabilityBadge } from '../Capability/CapabilityBadge';
import { heldCapabilityChanges } from '../Capability/heldCapabilities';
import { SkillsWithCapabilityField } from '../Capability/SkillsWithCapabilityField';
import { usePaginatedCapabilitiesForDepartments } from '../Capability/usePaginatedCapabilitiesForDepartments';
import { WorkerCollapsibleList } from './WorkerCollapsibleList';

const FORM_ID = 'bulk-edit-worker-skills-form';

const skillOptionSchema = z.object({ value: z.number(), label: z.string() });

const validationSchema = z.object({
  skillsToAdd: z.array(skillOptionSchema),
  skillsToRemove: z.array(skillOptionSchema)
});

type FormValues = z.input<typeof validationSchema>;

type Worker = Pick<ServiceWorkerListRecord, 'id' | 'firstName' | 'lastName' | 'skillIds'>;

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  workers: Worker[];
  onSave: () => void;
  /** Names in the capability-change lists open this worker. */
  workerTo?: (serviceWorkerId: number) => string;
};

export const BulkEditWorkerSkillsSheet = ({ open, onOpenChange, workers, onSave, workerTo }: Props) => {
  const { data: allCapabilities } = usePaginatedCapabilitiesForDepartments({ paginated: false, enabled: open });

  const {
    mutateAsync: patchBulkServiceWorkers,
    isPending,
    isSuccess
  } = noddiAsync.usePatch({
    type: URLKeys.patchBulkServiceWorkers,
    queryConfig: {
      onSuccess: async () => {
        invalidateQuery({ urlKey: URLKeys.getWorker });
        await invalidateQuery({ urlKey: URLKeys.getPaginatedServiceWorkers });
        onSave();
      }
    }
  });

  const defaultValues: FormValues = { skillsToAdd: [], skillsToRemove: [] };
  const form = useAppForm({
    defaultValues,
    validators: { onSubmit: validationSchema },
    onSubmit: async ({ value }) => {
      await patchBulkServiceWorkers({
        ids: workers.map((worker) => worker.id),
        skillIdsToAdd: value.skillsToAdd.map((skill) => skill.value),
        skillIdsToRemove: value.skillsToRemove.map((skill) => skill.value)
      });
    }
  });

  const skillsToAdd = useStore(form.store, (state) => state.values.skillsToAdd);
  const skillsToRemove = useStore(form.store, (state) => state.values.skillsToRemove);
  const removeIds = new Set(skillsToRemove.map((skill) => skill.value));
  const capabilities = allCapabilities?.results ?? [];

  const workerChanges = workers.map((worker) => ({
    worker,
    ...heldCapabilityChanges(
      capabilities,
      worker.skillIds,
      [...worker.skillIds, ...skillsToAdd.map((skill) => skill.value)].filter((id) => !removeIds.has(id))
    )
  }));
  const changes = capabilities
    .map((capability) => ({
      capability,
      gaining: workerChanges.filter((c) => c.gained.includes(capability)).map((c) => c.worker),
      losing: workerChanges.filter((c) => c.lost.includes(capability)).map((c) => c.worker)
    }))
    .filter((change) => change.gaining.length > 0 || change.losing.length > 0);

  return (
    <form.AppForm>
      <NoddiSheet
        open={open}
        onOpenChange={(next) => {
          onOpenChange(next);
          if (!next) {
            form.reset();
          }
        }}
        title='Edit skills'
        description={`Editing ${pluralizeWithValue({ value: workers.length, word: 'selected worker' })}`}
        isLoading={isPending}
        isSuccess={isSuccess}
        successTitle='Skills updated'
        successDescription='Capabilities follow the new skills.'
        side='right'
        footer={<form.SubscribeButton className='w-full' formId={FORM_ID} label='Save changes' startIcon='Save' />}
      >
        <form
          id={FORM_ID}
          className='flex flex-col gap-4'
          onSubmit={(e) => {
            e.preventDefault();
            e.stopPropagation();
            form.handleSubmit();
          }}
        >
          <form.AppField name='skillsToAdd'>{() => <SkillsWithCapabilityField label='Skills to add' />}</form.AppField>
          <form.AppField name='skillsToRemove'>
            {() => <TanstackSkillsAsyncMultiSelect label='Skills to remove' />}
          </form.AppField>
          <NoddiCard
            size='small'
            title='Capability changes'
            titleCount={changes.length || undefined}
            description={changes.length === 0 ? 'No worker gains or loses a capability.' : undefined}
            contentClassName='flex flex-col gap-2'
          >
            {changes.map(({ capability, gaining, losing }) => (
              <NoddiBasicCard key={capability.id} size='small' className='flex flex-col gap-2'>
                <CapabilityBadge capability={capability} />
                {gaining.length > 0 && (
                  <WorkerCollapsibleList
                    title={`${pluralizeWithValue({ value: gaining.length, word: 'worker' })} will gain it`}
                    workers={gaining}
                    workerTo={workerTo}
                  />
                )}
                {losing.length > 0 && (
                  <WorkerCollapsibleList
                    title={`${pluralizeWithValue({ value: losing.length, word: 'worker' })} will lose it`}
                    workers={losing}
                    workerTo={workerTo}
                  />
                )}
              </NoddiBasicCard>
            ))}
          </NoddiCard>
        </form>
      </NoddiSheet>
    </form.AppForm>
  );
};
